import React, { useRef } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import PasswordReset from './pages/PasswordReset';
import Logout from './pages/Logout';
import Register from './pages/Register';
import Header from './components/Header';
import Footer from './components/Footer';
import CsvUploader from './components/CSVUploader';
import ProductPage from './pages/ProductPage';
import { AuthProvider } from './context/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider, useCart } from './context/cartContext';
import { CustomerProvider } from './context/customerContext';

import Cart from './pages/Cart';
import FloatingCart from './components/FloatingCart';
// import Order from './pages/Order'; // Import the new component
import OrderEdit from './pages/OrderEdit'; // Import the new component
import OrderDetails from './pages/OrderDetails'; // Import the new component
import OrderHistory from './pages/OrderHistory'; // Import the new component
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import the CSS
import { ConfigProvider } from './context/ConfigContext';
import CustomerManagement from './pages/CustomerManagement'; // <-- Import the new page
import { LocationProvider } from './context/LocationContext';
import { GoogleOAuthProvider } from '@react-oauth/google'; // Import GoogleOAuthProvider
import InvoiceDetails from './pages/InvoiceDetails';
import InvoiceHistory from './pages/InvoiceHistory';
import StockPage from './pages/Stock';
import QrGenerator from './pages/QrGenerator';
import QrScanner from './pages/QrScanner';
import ScanHistoryDetails from './components/ScanHistoryDetails';
import ProtectedRoute from './components/ProtectedRoute';
import 'leaflet/dist/leaflet.css';
import GpsTracker from './components/GpsTracker'; // Import the GpsTracker component
import { StockLocationProvider } from './context/StockLocationContext';
import DashBoard from './pages/MyInvois/DashBoard';

import TaskReminder from './pages/TaskReminder/TaskReminder';



const googleClientId = window.APP_CONFIG?.REACT_APP_GOOGLE_CLIENT_ID;



// New component to conditionally render FloatingCart
const ConditionalFloatingCart = ({ cartRef }: { cartRef: React.RefObject<HTMLDivElement> }) => {
  const { loading } = useCart();
  return loading ? null : <FloatingCart cartRef={cartRef} />; // Pass cartRef here
};


// Function to determine basename dynamically
const getDynamicBasename = (): string => {
  const currentPathname = window.location.pathname;
  // Check if the path starts with /shop/ or is exactly /shop (in case of no trailing slash)
  if (currentPathname.startsWith('/shop/') || currentPathname === '/shop') {
    return '/shop';
  }
  // Otherwise, assume it's served from the root
  return '/';
};


const App = () => {

  const cartRef = useRef<HTMLDivElement>(null);

  const dynamicBasename = getDynamicBasename(); // Get basename dynamically

  if (!googleClientId) {
    console.error("FATAL ERROR: Google Client ID is not defined. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file.");
    // Optionally render an error message to the user or prevent app initialization
    return (
      <div style={{ color: 'red', textAlign: 'center', marginTop: '20px', padding: '20px', border: '1px solid red' }}>
        Application configuration error: Google Client ID is missing.
        <br />
        Google Login cannot be initialized. Please check the console for more details.
      </div>
    );
  }


  return (
    <ConfigProvider>
      <HelmetProvider>
        <GoogleOAuthProvider clientId={googleClientId}>
          {/* Add the basename prop here */}
          <BrowserRouter basename={dynamicBasename} future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <AuthProvider>
              <CartProvider>
                <CustomerProvider>
                  <LocationProvider>
                    <StockLocationProvider>

                      <div className="app-most-top flex flex-col min-h-dvh min-w-full">

                        {/* HeaderWrapper might need adjustment if it relies on exact '/' path */}
                        <HeaderWrapper />
                        <main className="flex-grow">
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/product/:productId/:productName?" element={<ProductPage />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password/:token" element={<PasswordReset />} />
                            <Route path="/logout" element={<Logout />} />
                            <Route path="/csv-uploader" element={<CsvUploader />} />
                            <Route path="/cart" element={<Cart />} />
                            {/* <Route path="/new-order" element={<Order />} /> */}
                            <Route path="/new-order" element={<OrderEdit />} />
                            {/* Add this new route for draft orders */}
                            <Route path="/new-order/draft/:orderId" element={<OrderEdit />} />

                            <Route path="/order-history" element={<OrderHistory />} />
                            <Route path="/order/:orderId" element={<OrderDetails />} />
                            <Route path="/invoice-history" element={<InvoiceHistory />} />
                            <Route path="/invoices/:invoice_id" element={<InvoiceDetails />} />

                            <Route path="/customer-management" element={<CustomerManagement />} /> {/* <-- Add this route */}

                            {/* Protected Routes */}
                            <Route element={<ProtectedRoute />}>
                              <Route path="/stock-management" element={<StockPage />} />
                              <Route path="/qr-generator" element={<QrGenerator />} />
                              <Route path="/qr-scanner" element={<QrScanner />} />
                              <Route path="/scan-history/:movement_type/:id" element={<ScanHistoryDetails />} />
                            </Route>

                            {/* Protected Routes */}
                            <Route element={<ProtectedRoute />}>
                              <Route path="/task-reminder" element={<TaskReminder />} />
                            </Route>

                            {/* MyInvois Protected Routes */}
                            <Route element={<ProtectedRoute />}>
                              <Route path="/myinvois/dashboard" element={<DashBoard />} />
                            </Route>

                            <Route path="/gps-tracker" element={<GpsTracker />} />

                            <Route path="*" element={<div className="text-center mt-20"><a href='/'>Page Not Found. Back to Home</a></div>} />

                            {/* Fallback route for 404 */}


                          </Routes>
                        </main>
                        <FloatingCart cartRef={cartRef} />
                        <Footer />
                        <ToastContainer
                          position="top-center"
                          autoClose={5000}
                          hideProgressBar={false}
                          newestOnTop={false}
                          closeOnClick
                          rtl={false}
                          pauseOnFocusLoss
                          draggable
                          pauseOnHover
                          theme="light"
                        />
                      </div>

                    </StockLocationProvider>
                  </LocationProvider>
                </CustomerProvider>
              </CartProvider>
            </AuthProvider>
          </BrowserRouter>
        </GoogleOAuthProvider>
      </HelmetProvider>
    </ConfigProvider>
  );
};

// --- Adjust HeaderWrapper if needed ---
// If you want the header hidden ONLY at the absolute root (/shop/),
// this logic might need adjustment depending on how HeaderWrapper uses location.pathname
const HeaderWrapper = () => {
  const location = useLocation();
  // This will now hide the header only when the path is exactly '/shop/'
  // If you intended to hide it at the logical "home" (which is now /shop/), this is correct.
  const shouldHideHeader = location.pathname === '/'; // React Router sees '/' relative to the basename
  return shouldHideHeader ? null : <Header />;
};


export default App;
